/**
 * CommunityHeader — banner, identity, stats, join action, more menu.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Globe, Lock, Mail, MoreHorizontal, Share2, Flag, Settings, MessageSquareText } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { VISIBILITY_META, ROLE_LABELS } from './constants';
import { formatCount } from './utils/communityHelpers';
import JoinButton from './JoinButton';
import { COMMUNITIES } from '@/constants/testIds';

const spineTone = { coral: 'from-coral to-[#b85e58]', yellow: 'from-yellow to-[#e0b164]', sage: 'from-sage to-[#a9c39d]', blue: 'from-blue to-[#6b808b]' };
const VISIBILITY_ICON = { public: Globe, private: Lock, restricted: Mail };

export default function CommunityHeader({ community, role, onOpenChat }) {
  const Icon = VISIBILITY_ICON[community.visibility] ?? Globe;
  const isModerator = role === 'admin' || role === 'moderator';

  return (
    <header className="relative">
      <div className={cx('h-36 w-full bg-gradient-to-br sm:h-48', spineTone[community.tone] ?? spineTone.coral)} aria-hidden="true" />

      <div className="u-container relative -mt-12 sm:-mt-14">
        <Link to="/app/communities" className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-subtle hover:text-coral">
          <ArrowLeft className="h-3.5 w-3.5" /> All Communities
        </Link>

        <div className="flex flex-col gap-4 rounded-2xl border border-line bg-card p-5 shadow-card sm:flex-row sm:items-end sm:gap-5">
          <span className={cx('grid h-20 w-20 shrink-0 place-items-center rounded-2xl bg-gradient-to-br font-display text-3xl font-bold text-[#FFF7F0] shadow-card ring-4 ring-card', spineTone[community.tone] ?? spineTone.coral)}>
            {community.name[0]}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">{community.name}</h1>
              <Icon className="h-4 w-4 shrink-0 text-subtle" aria-label={VISIBILITY_META[community.visibility]?.label} />
              {role && <span className="rounded-full bg-coral/10 px-2 py-0.5 text-[10px] font-semibold text-coral">{ROLE_LABELS[role]}</span>}
            </div>
            <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-subtle">{community.tagline}</p>
            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-subtle">
              <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {formatCount(community.memberCount)} members</span>
              {community.activeNow > 0 && (
                <span className="inline-flex items-center gap-1.5 font-medium text-ink">
                  <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-sage" /></span>
                  {formatCount(community.activeNow)} active now
                </span>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button" onClick={onOpenChat}
              className="hidden items-center gap-1.5 rounded-full border border-line px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-coral/40 hover:text-coral sm:inline-flex"
            >
              <MessageSquareText className="h-4 w-4" /> Chat
            </button>
            <JoinButton community={community} data-testid={COMMUNITIES.joinButton} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-line text-subtle transition-colors hover:border-coral/40 hover:text-ink" aria-label="More options">
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-xl border-line bg-card p-1.5 text-ink shadow-xl">
                <DropdownMenuItem onSelect={() => toast('Link copied')} className="cursor-pointer gap-2.5 rounded-lg text-sm">
                  <Share2 className="h-3.5 w-3.5 text-subtle" /> Share Community
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => toast('Report submitted — thank you')} className="cursor-pointer gap-2.5 rounded-lg text-sm">
                  <Flag className="h-3.5 w-3.5 text-subtle" /> Report Community
                </DropdownMenuItem>
                {isModerator && (
                  <>
                    <DropdownMenuSeparator className="bg-line" />
                    <DropdownMenuItem onSelect={() => toast('Community settings coming soon')} className="cursor-pointer gap-2.5 rounded-lg text-sm">
                      <Settings className="h-3.5 w-3.5 text-subtle" /> Community Settings
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
